import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { first } from 'rxjs';
import { UploadReturn } from 'src/app/core/services/base/document.service';
import { ClaimDocumentService } from 'src/app/core/services/claim_document.service';
import { PersonalDocumentService } from 'src/app/core/services/personal_document.service';

export interface FileWrapper {
  file: File;
  upload: number;
}

@Component({
  selector: 'app-document-upload-modal',
  templateUrl: './document-upload-modal.component.html',
  styleUrls: ['./document-upload-modal.component.scss'],
})
export class DocumentUploadModalComponent {
  URL = 'path_to_api';
  boxFile: string = '';
  sendFile: string = '';
  successFile: string = '';
  errorFile: string = '';
  files?: File[];
  backgroundImage: string | undefined = '';

  filesToRender: {
    src: string;
    isPdf: boolean;
    name: string;
  }[] = [];
  showFilesPreview = false;
  maxFileError = false;

  result: any = {};

  uploadStatus: UploadReturn[] = [];

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  isDragOver = false;

  maxFileSize!: number;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      bucket: string;
      name: string;
      type: 'PERSONAL' | 'CLAIM';
      multiple: boolean;
      ref: string;
      // Optional question id to associate uploaded documents with a questionnaire question
      question_id?: string;
      maxFileSize?: number;
      maxFiles?: number;
    },
    public dialog: MatDialog,
    private pds: PersonalDocumentService,
    private cds: ClaimDocumentService,
    private dialogRef: MatDialogRef<DocumentUploadModalComponent>,
    private snackBar: MatSnackBar
  ) {
    console.log('DocumentUploadModal -> this', this);
    this.result = {
      status: 'CANCELLED',
      value: null,
    };

    const sub = this.dialogRef
      .backdropClick()
      .pipe(first())
      .subscribe((e) => this.close());

    this.maxFileSize = this.data.maxFileSize ?? 1024 * 1024;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  get maxFileSizeFormatted(): string {
    return this.formatFileSize(this.maxFileSize);
  }

  close() {
    console.log('On Modal', this.result);
    this.dialogRef.close(JSON.stringify(this.result));
  }

  fileChange(ev: any) {
    const files: File[] = Array.from(ev.target?.files || ev);
    this.filesToRender = [];
    this.showFilesPreview = false;
    if (files && Array.isArray(files)) {
      if (this.data.maxFiles && files.length > this.data.maxFiles) {
        this.boxFile = '2';

        this.showErrorSnackbar(
          `Too many files selected. Maximum allowed: ${this.data.maxFiles} file(s).`,
          true
        );
        return;
      }
      this.files = files;
      this.showFilesPreview = files.length > 0;
      files.forEach((file, index) => {
        this.filesToRender.push({
          src: '',
          isPdf: file.type == 'application/pdf',
          name: file.name,
        });
        console.log('file ', index, file);
        this.boxFile = '2';

        this.getBase64(file)
          .then((res) => {
            this.filesToRender[index].src = String(res);
          })
          .catch((rej) => {
            console.log(rej);
          });
      });
    }
  }

  getBase64(file: File) {
    return new Promise((res, rej) => {
      var reader = new FileReader();
      reader.onload = function () {
        res(reader.result);
      };
      reader.onerror = function (error) {
        console.log('Error: ', error);
        rej(error);
      };
      reader.readAsDataURL(file);
    });
  }

  async uploadFile() {
    try {
      this.showFilesPreview = false;
      this.sendFile = '2';
      this.errorFile = '';
      this.maxFileError = false;
      let uls: ClaimDocumentService | PersonalDocumentService;
      this.uploadStatus = [];

      if (!this.files) {
        this.showErrorSnackbar('No file selected.');
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg'];

      const invalidFiles = Array.from(this.files).filter(
        (file) => !allowedTypes.includes(file.type)
      );

      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map((f) => f.name).join(', ');
        this.showErrorSnackbar(
          `Unsupported file type: ${invalidFileNames}. Only PDF and JPG files are allowed.`
        );
        return;
      }

      const oversizedFiles = Array.from(this.files).filter(
        (file) => file.size > this.maxFileSize
      );

      if (oversizedFiles.length > 0) {
        this.showErrorSnackbar(
          `Maximum file size allowed is ${this.maxFileSizeFormatted}.`
        );
        return;
      }

      if (this.data.type == 'CLAIM') {
        uls = this.cds;
      } else {
        uls = this.pds;
      }

      if (this.data.multiple) {
        for (let i = 0; i < this.files.length; i++) {
          const f = this.files[i];
          try {
            const a = await uls.upload(
              f,
              this.data.ref,
              this.data.name,
              this.data.bucket,
              i,
              // forward optional question id when provided
              this.data.question_id
            );
            this.uploadStatus.push(a);
          } catch (uploadError) {
            console.error(`Error uploading file ${f.name}:`, uploadError);
            this.showErrorSnackbar(`Failed to upload file ${f.name}`);
            return;
          }
        }
      } else {
        const f = this.files[0];
        try {
          const a = await uls.upload(
            f,
            this.data.ref,
            this.data.name,
            this.data.bucket,
            undefined,
            // forward optional question id when provided
            this.data.question_id
          );
          this.uploadStatus.push(a);
        } catch (uploadError) {
          console.error(`Error uploading file ${f.name}:`, uploadError);
          this.showErrorSnackbar(`Failed to upload file ${f.name}`);
          return;
        }
      }

      this.successFile = '1';
      this.sendFile = '';

      this.result.value = this.uploadStatus.map((e) => e.data?.path);
      this.result.status = 'COMPLETED';
    } catch (error) {
      console.error('Error during upload:', error);

      this.sendFile = '';
      this.successFile = '';

      this.errorFile = '1';

      this.result.status = 'ERROR';
      this.result.error = 'Error during upload';

      this.showErrorSnackbar('Error during upload');
    }
  }

  private showErrorSnackbar(message: string, maxFileError?: boolean) {
    this.sendFile = '';
    this.successFile = '';
    this.errorFile = maxFileError ? '' : '1';
    this.maxFileError = maxFileError || false;

    this.result.status = 'ERROR';
    this.result.error = message;

    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }

  openDialog() {
    const dialogRef = this.dialog.open(DocumentUploadModalComponent);

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);
    });
  }

  onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer && event.dataTransfer.files) {
      this.fileChange(event.dataTransfer.files);
    }
  }

  onContainerClick(event: MouseEvent) {
    const tag = (event.target as HTMLElement).tagName.toLowerCase();
    if (tag === 'label' || tag === 'input' || tag === 'button') return;
    this.fileInput?.nativeElement.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }
}
function decode(arg0: string): any {
  throw new Error('Function not implemented.');
}
